// Libs
import { createSlice } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { Action } from 'redux/types/Action';
// App
import { ShopProductSummaryArray } from 'app-shared/lib/api/shared';
import {
  GetShopProductsFilters as CatalogGetShopProductsFilters,
  GetShopsFilters as CatalogGetShopFilters,
  ShopSummary,
} from 'app-shared/lib/api/catalog/shop';
import { GetShopProductsFilters as ShopAdminGetShopProductsFilters } from 'app-shared/lib/api/shop-administration/shop';
import { PaginationState } from 'app-shared/lib/api/utils/pagination.interfaces';
import { AutocompletePredictionWithDetails } from 'app-shared/lib/components/inputs/places-search-autocomplete';

export type CachableSummaries = ShopProductSummaryArray | ShopSummary[];
export type CachableFiltersQueryParams =
  | CatalogGetShopFilters
  | CatalogGetShopProductsFilters
  | ShopAdminGetShopProductsFilters;

/**
 * Interface definition for state
 */
interface State {
  /**
   * Stores the position of scroll to restore it when hitting back button
   * This must be updated in all candidates for cached pages
   */
  scrollPosition: number;

  /**
   * Stores shop summaries list for restoring it after hitting back button
   * This must be updated in all candidates for cached pages
   */
  summaries: CachableSummaries;

  /**
   * Stores shop summaries list for restoring it after hitting back button
   * This must be updated in all candidates for cached pages
   */
  paginationState: PaginationState;

  /**
   * Stores filters query params for restoring content after hitting back button
   * This must be updated in all candidates for cached pages
   */
  filtersQueryParams: CachableFiltersQueryParams;

  /**
   * Stores place for restoring content after hitting back button
   * This must be updated in all candidates for cached pages
   */
  place?: AutocompletePredictionWithDetails;
}

/**
 * Type definition for state
 */
export type SummariesCacheState = Readonly<State>;

/**
 * Initial state definition
 */
const initialState: State = {
  scrollPosition: null,
  summaries: null,
  paginationState: null,
  filtersQueryParams: null,
  place: null,
};

/**
 * Reducer definition
 */
const reducers = {
  setSummariesCache: (state: State, action: Action<SummariesCacheState>) => {
    state.scrollPosition = action.payload.scrollPosition;
    state.summaries = action.payload.summaries;
    state.paginationState = action.payload.paginationState;
    state.filtersQueryParams = action.payload.filtersQueryParams;
    state.place = action.payload.place;
  },
  setSummaries: (state: State, action: Action<CachableSummaries>) => {
    state.summaries = action.payload;
  },
  clearSummariesCache: (state: State) => {
    state.scrollPosition = null;
    state.summaries = null;
    state.paginationState = null;
    state.filtersQueryParams = null;
    state.place = null;
  },
};

/**
 * Slice definition
 */
const summariesCache = createSlice({
  name: 'summariesCache',
  initialState,
  reducers,
});

/**
 * Extract user navigation cache root state
 * @param   {Object} state The root state
 * @returns {State} The user preferences
 */
export const selectSummariesCache = (state: Readonly<RootState>): SummariesCacheState => state.summariesCache;

/**
 * Actions definition
 */
export const { setSummariesCache, setSummaries, clearSummariesCache } = summariesCache.actions;

export default summariesCache.reducer;
